import React, { useEffect, useState, useCallback, useMemo } from "react";
import AIContainer from "./AIAgent/AIContainer";
import { widgetRegistry } from "./widgets";
import VideoContainer from "../components/Videos/VideoContainer";
import { progressService } from "../../../services/progressService";
import Breadcrumb from "./Breadcrumb";
import AccountabilityContainer from "./Accountability/AccountabilityContainer";
import CoachContainer from "../components/Coach/CoachContainer";

const SubsectionContent = ({
  subsection,
  userId,
  completed = false,
  onProgressUpdate,
  onComplete,
  selectedCourse,
  selectedModule,
  onGoToCourse,
  onGoToModule,
  onWidgetPhaseChange, // new API: "intro" | "immersive" | "full"
  onToggleImmersive,   // old API: true | false
  onSelectModule,
  onSelectSubsection,
}) => {
  const [widgetInitialData, setWidgetInitialData] = useState(null);
  const [primaryAction, setPrimaryAction] = useState({ visible: false });
  const [phase, setPhase] = useState("intro");
  const [sectionViewReset, setSectionViewReset] = useState(0);


  /* ---------------- Legacy Compatibility Adapter ---------------- */
  // Translate old onToggleImmersive(boolean) → new onWidgetPhaseChange(phase)
const handlePhaseChange = useCallback(
  (phaseOrBool) => {
    let resolvedPhase = typeof phaseOrBool === "string"
      ? phaseOrBool
      : phaseOrBool
      ? "immersive"
      : "intro";

    setPhase(resolvedPhase); // ✅ track the current mode
    onWidgetPhaseChange?.(resolvedPhase);
    if (typeof onToggleImmersive === "function") {
      onToggleImmersive(phaseOrBool);
    }
  },
  [onWidgetPhaseChange, onToggleImmersive]
);

  const resetPhaseToIntro = useCallback(() => {
    setPhase("intro");
    onWidgetPhaseChange?.("intro");
    if (typeof onToggleImmersive === "function") {
      onToggleImmersive(false);
    }
  }, [onToggleImmersive, onWidgetPhaseChange]);

  const resetSectionIntroView = useCallback(() => {
    setSectionViewReset((prev) => prev + 1);
    resetPhaseToIntro();
  }, [resetPhaseToIntro]);

  const handleGoToCourse = useCallback(() => {
    resetPhaseToIntro();
    onGoToCourse?.();
  }, [onGoToCourse, resetPhaseToIntro]);

  const handleGoToModule = useCallback(() => {
    resetSectionIntroView();
    onGoToModule?.();
  }, [onGoToModule, resetSectionIntroView]);

  /* ---------- Load user progress ---------- */
  useEffect(() => {
    if (!subsection?.id) return;

    progressService
      .getUserProgress(userId, subsection.id)
      .then((data) => setWidgetInitialData(data?.data || null))
      .catch(() => setWidgetInitialData(null));

    progressService.updateUserState(userId, subsection.id).catch(() => {});
  }, [userId, subsection?.id]);

  /* ---------- Handle subsection change ---------- */
  const handleSubsectionChange = useCallback(
    (newSubsectionId) => {
      if (!newSubsectionId || !selectedCourse) return;
      const modules = selectedCourse?.modules || [];
      let foundSubsection = null;
      let foundModule = null;

      for (const module of modules) {
        const sub = module.subsections?.find((s) => s.id === newSubsectionId);
        if (sub) {
          foundSubsection = sub;
          foundModule = module;
          break;
        }
      }

      if (foundSubsection && foundModule) {
        onComplete?.(null, foundSubsection);
      }
    },
    [selectedCourse, onComplete]
  );

  /* ---------- Primary Action Helpers ---------- */
  const setPrimary = useCallback((cfg) => {
    setPrimaryAction((prev) => ({
      ...prev,
      ...cfg,
      visible: cfg?.visible !== false,
    }));
  }, []);

  const clearPrimary = useCallback(() => setPrimaryAction({ visible: false }), []);
  const actions = useMemo(
    () => ({ setPrimary, clearPrimary }),
    [setPrimary, clearPrimary]
  );

  useEffect(() => {
    return () => {
      actions?.clearPrimary?.();
    };
  }, [actions, subsection?.id]);

  const WidgetComponent =
    subsection.type === "widget"
      ? widgetRegistry[subsection.subsection_url] || null
      : null;
  const widgetKey = `${subsection?.id ?? "widget"}-${sectionViewReset}`;

  /* ---------- Layout ---------- */
  return (
    <div className="w-full h-full flex flex-col">
      <div className="w-full h-full flex flex-col">
{/* 🧭 Breadcrumb — hidden in full mode */}
{phase !== "full" && (
  <Breadcrumb
    course={selectedCourse}
    module={selectedModule}
    subsection={subsection}
    onGoToCourse={handleGoToCourse}
    onGoToModule={handleGoToModule}
  />
)}


        {/* 🧩 Widget */}
        {WidgetComponent && (
          <WidgetComponent
            key={widgetKey}
            userId={userId}
            subsectionId={subsection.id}
            initialData={widgetInitialData}
            onComplete={onComplete}
            onProgressUpdate={onProgressUpdate}
            onSubsectionChange={handleSubsectionChange}
            onToggleImmersive={handlePhaseChange}   // ✅ unified handler
            onPhaseChange={handlePhaseChange}       // ✅ unified handler
            actions={actions}
            unmetNeedsData={[]}
            selectedCards={[]}
            course={selectedCourse}
            onSelectModule={onSelectModule}
            onSelectSubsection={onSelectSubsection}
          />
        )}

        {/* 🎥 Video */}
        {subsection.type === "video" && subsection.subsection_url && (
          <VideoContainer
            userId={userId}
            subsection={subsection}
            onProgressUpdate={onProgressUpdate}
            course={selectedCourse}
            module={selectedModule}
            actions={actions}
            onToggleImmersive={handlePhaseChange}
            onPhaseChange={handlePhaseChange}
          />
        )}

        {/* 🤖 AI Chat */}
        {subsection.type === "ai" && (
          <AIContainer
            key={widgetKey}
            userId={userId}
            subsection={subsection}
            actions={actions}
            onToggleImmersive={handlePhaseChange}
            onPhaseChange={handlePhaseChange}
            onSubsectionChange={handleSubsectionChange}
            currentModule={
              typeof selectedModule?.order === "number"
                ? selectedModule.order
                : selectedModule?.id ?? null
            }
            onComplete={onComplete}
            completed={completed}
          />
        )}

        {/* 🧑‍🏫 Human Coach */}
        {subsection.type === "human_coach" && (
          <CoachContainer
            userId={userId}
            course={selectedCourse}
            module={selectedModule}
            subsection={subsection}
            onProgressUpdate={onProgressUpdate}
            actions={actions}
            onToggleImmersive={handlePhaseChange}
            onPhaseChange={handlePhaseChange}
          />
        )}

        {/* ✅ Accountability */}
        {subsection.type === "accountability" && (
          <AccountabilityContainer
            userId={userId}
            subsection={subsection}
            onProgressUpdate={onProgressUpdate}
            onComplete={onComplete}
            actions={actions}
          />
        )}
      </div>

      {/* ⚡ Primary Action Bar */}
      {primaryAction.visible && (
        <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 lg:mt-32 2xl:mt-0 py-3 px-6 flex justify-end">
          <button
            type="button"
            onClick={primaryAction.onClick}
            disabled={primaryAction.disabled || primaryAction.loading}
            className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-white font-semibold ${
              primaryAction.disabled || primaryAction.loading
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-[#54B0AF] hover:bg-teal-600"
            }`}
          >
            {primaryAction.loading ? "Saving..." : primaryAction.label || "Next"}
          </button>
        </div>
      )}
    </div>
  );
};

export default SubsectionContent;
